import { State } from "..";

import { AbstractStore } from ".";

/**
 * Possible noise suppresion states. Browser is browser noise suppresion, enhanced is machine learning suppression via RNNoise
 * and dpdfnet (Konkord) is machine learning suppression via DPDFNet2 (max quality).
 * deepfilter (DeepFilterNet3) is only read from older settings, it becomes dpdfnet.
 */
export type NoiseSuppresionState =
  | "disabled"
  | "browser"
  | "enhanced"
  | "dpdfnet"
  | "deepfilter";

const NoiseSuppresionStates: NoiseSuppresionState[] = [
  "disabled",
  "browser",
  "enhanced",
  "dpdfnet",
  "deepfilter",
];

/**
 * Possible screen share qualities. Low is 720p@30fps, high 1080p@30fps, text is source@5fps
 * and ultra (Konkord voice only) is 1440p@60fps.
 */
export type ScreenShareQualityName =
  | "low"
  | "high"
  | "text"
  | "hd60"
  | "ultra"
  // Konkord: 720p 60 and 1440p 30, so every resolution has both frame rates
  | "low60"
  | "ultra30";

/**
 * Array of available screen share quality names.
 */
export const ScreenShareQualityNames: ScreenShareQualityName[] = [
  "low",
  "high",
  "text",
  "hd60",
  "ultra",
  "low60",
  "ultra30",
];

/**
 * Konkord: DeepFilterNet3 attenuation limit used until the user picks one
 */
const DEFAULT_NOISE_SUPPRESSION_STRENGTH = 30;

export interface TypeVoice {
  preferredAudioInputDevice?: string;
  preferredAudioOutputDevice?: string;
  preferredVideoDevice?: string;

  echoCancellation: boolean;
  noiseSupression: NoiseSuppresionState;
  /** Konkord: DeepFilterNet3 attenuation limit in dB (0 = no reduction, 100 = full) */
  noiseSuppressionStrength: number;
  /** Konkord: the strength was picked by the user (not the old 100 dB default) */
  noiseSuppressionTuned?: boolean;
  /** Konkord: close the mic between words (after noise suppression) */
  noiseGate: boolean;
  /** Konkord: only send the microphone while the push to talk key is held */
  pushToTalk: boolean;
  /** Konkord: KeyboardEvent.code of the push to talk key, or "Mouse4" / "Mouse5" */
  pushToTalkKey?: string;
  /**
   * Konkord: hold a key to mute the microphone (talk in a game's own voice
   * chat without being heard here); only while push to talk is off
   */
  pushToMute: boolean;
  /** Konkord: KeyboardEvent.code of the push to mute key, or "Mouse3/4/5" */
  pushToMuteKey?: string;
  autoGainControl: boolean;

  screenShareQuality: ScreenShareQualityName;
  screenShareQualityAsk: boolean;
  screenShareAudio: boolean;
  /** Konkord: streams go straight between computers when possible (peer to peer) */
  directStream: boolean;
  /**
   * Konkord: most people who watch our stream directly (1 to 5), at any
   * quality, changed live (Hudson 31/10; before, in economy mode only)
   */
  streamViewers: number;

  inputVolume: number;
  outputVolume: number;
  /** Konkord: volume of the app's sounds (someone joined, stream started…), 0 to 1 */
  soundVolume: number;
  deafen: boolean;
  micOn: boolean;

  userVolumes: Record<string, number>;
  userMutes: Record<string, boolean>;

  screenShareVolumes: Record<string, number>;
  screenShareMutes: Record<string, boolean>;

  /** Konkord desktop: programs whose sound stays out of our streams (by name) */
  streamAppsOff: string[];
  /** Konkord: volume of single programs in others' streams, by "<user id>/<program>" */
  streamAppVolumes: Record<string, number>;
  /** Konkord: programs muted in others' streams, by "<user id>/<program>" */
  streamAppMutes: Record<string, boolean>;
}

/**
 * Konkord: stream sound volumes go up to 300%
 */
const clampVolume = (volume: number) => Math.max(0, Math.min(3, volume));

/** Konkord: a stream keeps 1 to 5 direct viewers (the streamer picks) */
export const clampStreamViewers = (n: number) =>
  Math.max(1, Math.min(5, Math.round(n)));

/**
 * Handles enabling and disabling client experiments.
 */
/**
 * Konkord: a computer too weak for the AI noise suppressor out of the box
 * (4 threads or fewer, or 4 GB of memory or less, when the browser says)
 */
export function weakComputer() {
  const threads = globalThis.navigator?.hardwareConcurrency ?? 8;
  const memory = (globalThis.navigator as { deviceMemory?: number } | undefined)
    ?.deviceMemory;
  return threads <= 4 || (memory !== undefined && memory <= 4);
}

/**
 * Konkord: the noise suppressor a new user starts with: the light one
 * (RNNoise) everywhere (Hudson 03/11: it handles a normal room, and the AI
 * one costs up to a third of a core while talking). The AI one (DPDFNet2)
 * can still be picked for noisy places, and a choice once saved is kept.
 * On WebKit the AI one is also untested (onnxruntime-web reports: out of
 * memory on iOS 18, a busy processor on Safari 26.2+)
 */
export function defaultNoiseSuppression(): NoiseSuppresionState {
  return "enhanced";
}

export class Voice extends AbstractStore<"voice", TypeVoice> {
  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "voice");
  }

  /**
   * Hydrate external context
   */
  hydrate(): void {
    /** nothing needs to be done */
  }

  /**
   * Generate default values
   */
  default(): TypeVoice {
    return {
      echoCancellation: true,
      // Konkord: the AI suppressor needs about a core; a weak computer (and
      // Safari, untested) starts with the light one (RNNoise), and can still
      // pick the AI one
      noiseSupression: defaultNoiseSuppression(),
      // Konkord: 100 dB (no limit) gated the voice too, cutting word endings
      // and sounding underwater; 30 dB still removes the noise
      noiseSuppressionStrength: DEFAULT_NOISE_SUPPRESSION_STRENGTH,
      autoGainControl: true,
      pushToTalk: false,
      pushToMute: false,
      noiseGate: true,
      screenShareQuality: "low",
      screenShareQualityAsk: true,
      screenShareAudio: true,
      directStream: true,
      streamViewers: 5,
      inputVolume: 1.0,
      outputVolume: 1.0,
      // Konkord: the sounds were found too loud at full volume
      soundVolume: 0.5,
      deafen: false,
      micOn: true,
      userVolumes: {},
      userMutes: {},
      screenShareVolumes: {},
      screenShareMutes: {},
      streamAppsOff: [],
      streamAppVolumes: {},
      streamAppMutes: {},
    };
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial<TypeVoice>): TypeVoice {
    const data = this.default();

    if (typeof input.preferredAudioInputDevice === "string") {
      data.preferredAudioInputDevice = input.preferredAudioInputDevice;
    }

    if (typeof input.preferredAudioOutputDevice === "string") {
      data.preferredAudioOutputDevice = input.preferredAudioOutputDevice;
    }

    if (typeof input.preferredVideoDevice === "string") {
      data.preferredVideoDevice = input.preferredVideoDevice;
    }

    if (typeof input.echoCancellation === "boolean") {
      data.echoCancellation = input.echoCancellation;
    }

    // migrate legacy noise suppression to new suppression state
    if ((input.noiseSupression as unknown) === "true") {
      data.noiseSupression = "enhanced";
    } else if ((input.noiseSupression as unknown) === "false") {
      data.noiseSupression = "disabled";
    } else if (
      input.noiseSupression &&
      NoiseSuppresionStates.includes(input.noiseSupression)
    ) {
      // Konkord: DeepFilterNet3 was replaced by DPDFNet2
      data.noiseSupression =
        input.noiseSupression === "deepfilter"
          ? "dpdfnet"
          : input.noiseSupression;
    }

    if (
      typeof input.noiseSuppressionStrength === "number" &&
      Number.isFinite(input.noiseSuppressionStrength)
    ) {
      data.noiseSuppressionStrength = Math.max(
        0,
        Math.min(100, Math.round(input.noiseSuppressionStrength)),
      );
    }

    // Konkord: everyone still on the old default moves to the new one once
    if (input.noiseSuppressionTuned === true) {
      data.noiseSuppressionTuned = true;
    } else if (data.noiseSuppressionStrength === 100) {
      data.noiseSuppressionStrength = DEFAULT_NOISE_SUPPRESSION_STRENGTH;
    }

    if (typeof input.autoGainControl === "boolean") {
      data.autoGainControl = input.autoGainControl;
    }

    if (typeof input.noiseGate === "boolean") {
      data.noiseGate = input.noiseGate;
    }

    if (typeof input.pushToTalk === "boolean") {
      data.pushToTalk = input.pushToTalk;
    }

    if (
      typeof input.pushToTalkKey === "string" &&
      /^[A-Za-z0-9]{1,32}$/.test(input.pushToTalkKey)
    ) {
      data.pushToTalkKey = input.pushToTalkKey;
    }

    if (typeof input.pushToMute === "boolean") {
      data.pushToMute = input.pushToMute;
    }

    if (
      typeof input.pushToMuteKey === "string" &&
      /^[A-Za-z0-9]{1,32}$/.test(input.pushToMuteKey)
    ) {
      data.pushToMuteKey = input.pushToMuteKey;
    }

    if (
      input.screenShareQuality &&
      ScreenShareQualityNames.includes(input.screenShareQuality)
    ) {
      data.screenShareQuality = input.screenShareQuality;
    }

    if (typeof input.screenShareQualityAsk === "boolean") {
      data.screenShareQualityAsk = input.screenShareQualityAsk;
    }

    if (typeof input.screenShareAudio === "boolean") {
      data.screenShareAudio = input.screenShareAudio;
    }

    if (typeof input.directStream === "boolean") {
      data.directStream = input.directStream;
    }

    if (
      typeof input.streamViewers === "number" &&
      Number.isFinite(input.streamViewers)
    ) {
      data.streamViewers = clampStreamViewers(input.streamViewers);
    }

    if (typeof input.inputVolume === "number") {
      data.inputVolume = input.inputVolume;
    }

    if (typeof input.outputVolume === "number") {
      data.outputVolume = input.outputVolume;
    }

    if (
      typeof input.soundVolume === "number" &&
      Number.isFinite(input.soundVolume)
    ) {
      data.soundVolume = Math.max(0, Math.min(1, input.soundVolume));
    }

    if (typeof input.deafen === "boolean") {
      data.deafen = input.deafen;
    }

    if (typeof input.micOn === "boolean") {
      data.micOn = input.micOn;
    }

    if (typeof input.userVolumes === "object") {
      Object.entries(input.userVolumes)
        .filter(
          ([userId, volume]) =>
            typeof userId === "string" && typeof volume === "number",
        )
        .forEach(([k, v]) => (data.userVolumes[k] = v));
    }

    if (typeof input.userMutes === "object") {
      Object.entries(input.userMutes)
        .filter(
          ([userId, muted]) => typeof userId === "string" && muted === true,
        )
        .forEach(([k, v]) => (data.userMutes[k] = v));
    }

    if (typeof input.screenShareVolumes === "object") {
      Object.entries(input.screenShareVolumes)
        .filter(
          ([userId, volume]) =>
            typeof userId === "string" && typeof volume === "number",
        )
        .forEach(([k, v]) => (data.screenShareVolumes[k] = v));
    }

    if (typeof input.screenShareMutes === "object") {
      Object.entries(input.screenShareMutes)
        .filter(
          ([userId, muted]) => typeof userId === "string" && muted === true,
        )
        .forEach(([k, v]) => (data.screenShareMutes[k] = v));
    }

    if (Array.isArray(input.streamAppsOff)) {
      data.streamAppsOff = input.streamAppsOff
        .filter((key) => typeof key === "string" && key.length <= 64)
        .slice(-100);
    }

    if (typeof input.streamAppVolumes === "object") {
      Object.entries(input.streamAppVolumes ?? {})
        .filter(
          ([key, volume]) =>
            key.length <= 128 &&
            typeof volume === "number" &&
            Number.isFinite(volume),
        )
        .forEach(([k, v]) => (data.streamAppVolumes[k] = clampVolume(v)));
    }

    if (typeof input.streamAppMutes === "object") {
      Object.entries(input.streamAppMutes ?? {})
        .filter(([key, muted]) => key.length <= 128 && muted === true)
        .forEach(([k, v]) => (data.streamAppMutes[k] = v));
    }

    return data;
  }

  /**
   * Set a user's volume
   * @param userId User ID
   * @param volume Volume
   */
  setUserVolume(userId: string, volume: number) {
    this.set("userVolumes", userId, volume);
  }

  /**
   * Get a user's volume
   * @param userId User ID
   * @returns Volume or default
   */
  getUserVolume(userId: string): number {
    // Konkord: ?? keeps a volume of 0 (|| turned it into 100%)
    return this.get().userVolumes[userId] ?? 1.0;
  }

  /**
   * Set whether a user is muted
   * @param userId User ID
   * @param muted Whether they should be muted
   */
  setUserMuted(userId: string, muted: boolean) {
    this.set("userMutes", userId, muted);
  }

  /**
   * Get whether a user is muted
   * @param userId User ID
   * @returns Whether muted
   */
  getUserMuted(userId: string): boolean {
    return this.get().userMutes[userId] || false;
  }

  /**
   * Set a user's screen share volume
   * @param userId User ID
   * @param volume Volume
   */
  setScreenShareVolume(userId: string, volume: number) {
    this.set("screenShareVolumes", userId, volume);
  }

  /**
   * Get a user's screen share volume
   * @param userId User ID
   * @returns Volume or default
   */
  getScreenShareVolume(userId: string): number {
    // Konkord: ?? keeps a volume of 0 (|| turned it into 100%)
    return this.get().screenShareVolumes[userId] ?? 1.0;
  }

  /**
   * Set whether a user's screen share is muted
   * @param userId User ID
   * @param muted Whether they should be muted
   */
  setScreenShareMuted(userId: string, muted: boolean) {
    this.set("screenShareMutes", userId, muted);
  }

  /**
   * Get whether a user's screen share is muted
   * @param userId User ID
   * @returns Whether muted
   */
  getScreenShareMuted(userId: string): boolean {
    // Konkord: streams are only received after clicking "watch", so their
    // sound plays unless muted (it started muted, every time the app opened)
    return this.get().screenShareMutes[userId] ?? false;
  }

  /**
   * Konkord desktop: whether a program's sound stays out of our streams
   */
  isStreamAppOff(key: string): boolean {
    return this.get().streamAppsOff.includes(key);
  }

  /**
   * Konkord desktop: leave a program's sound out of our streams (or not)
   */
  setStreamAppOff(key: string, off: boolean) {
    const list = this.get().streamAppsOff.filter((k) => k !== key);
    if (off) list.push(key);
    this.set("streamAppsOff", list.slice(-100));
  }

  /**
   * Konkord: volume of a program in someone's stream
   */
  getStreamAppVolume(userId: string, app: string): number {
    return this.get().streamAppVolumes[`${userId}/${app}`] ?? 1.0;
  }

  setStreamAppVolume(userId: string, app: string, volume: number) {
    this.set("streamAppVolumes", `${userId}/${app}`, clampVolume(volume));
  }

  /**
   * Konkord: whether a program in someone's stream is muted
   */
  getStreamAppMuted(userId: string, app: string): boolean {
    return this.get().streamAppMutes[`${userId}/${app}`] ?? false;
  }

  setStreamAppMuted(userId: string, app: string, muted: boolean) {
    this.set("streamAppMutes", `${userId}/${app}`, muted);
  }

  /**
   * Set the preferred audio input device
   */
  set preferredAudioInputDevice(value: string | undefined) {
    this.set("preferredAudioInputDevice", value);
  }

  /**
   * Set the preferred audio output device
   */
  set preferredAudioOutputDevice(value: string | undefined) {
    this.set("preferredAudioOutputDevice", value);
  }

  /**
   * Set the preferred video input device
   */
  set preferredVideoDevice(value: string | undefined) {
    this.set("preferredVideoDevice", value);
  }

  /**
   * Set echo cancellation
   */
  set echoCancellation(value: boolean) {
    this.set("echoCancellation", value);
  }

  /**
   * Set noise cancellation
   */
  set noiseSupression(value: NoiseSuppresionState) {
    this.set("noiseSupression", value);
  }

  /**
   * Set noise suppression strength (DeepFilterNet3 only)
   */
  set noiseSuppressionStrength(value: number) {
    this.set("noiseSuppressionTuned", true);
    this.set("noiseSuppressionStrength", value);
  }

  /**
   * Set auto gain control
   */
  set autoGainControl(value: boolean) {
    this.set("autoGainControl", value);
  }

  set noiseGate(value: boolean) {
    this.set("noiseGate", value);
  }

  set pushToTalk(value: boolean) {
    this.set("pushToTalk", value);
  }

  set pushToTalkKey(value: string | undefined) {
    this.set("pushToTalkKey", value);
  }

  set pushToMute(value: boolean) {
    this.set("pushToMute", value);
  }

  set pushToMuteKey(value: string | undefined) {
    this.set("pushToMuteKey", value);
  }

  /**
   * Set screen share quality
   */
  set screenShareQuality(value: ScreenShareQualityName) {
    this.set("screenShareQuality", value);
  }

  /**
   * Set screen share quality always ask
   */
  set screenShareQualityAsk(value: boolean) {
    this.set("screenShareQualityAsk", value);
  }

  /**
   * Set screen share audio
   */
  set screenShareAudio(value: boolean) {
    this.set("screenShareAudio", value);
  }

  set directStream(value: boolean) {
    this.set("directStream", value);
  }

  set streamViewers(value: number) {
    this.set("streamViewers", clampStreamViewers(value));
  }

  /**
   * Set input volume
   */
  set inputVolume(value: number) {
    this.set("inputVolume", value);
  }

  /**
   * Set output volume
   */
  set outputVolume(value: number) {
    this.set("outputVolume", value);
  }

  /**
   * Konkord: volume of the app's sounds (0 to 1)
   */
  set soundVolume(value: number) {
    this.set("soundVolume", Math.max(0, Math.min(1, value)));
  }

  get soundVolume(): number {
    return this.get().soundVolume ?? 0.5;
  }

  /**
   * Set mic status
   */
  set micOn(value: boolean) {
    this.set("micOn", value);
  }

  /**
   * Set deafen status
   */
  set deafen(value: boolean) {
    this.set("deafen", value);
  }

  /**
   * Get the preferred audio input device
   */
  get preferredAudioInputDevice(): string | undefined {
    return this.get().preferredAudioInputDevice;
  }

  /**
   * Get the preferred audio output device
   */
  get preferredAudioOutputDevice(): string | undefined {
    return this.get().preferredAudioOutputDevice;
  }

  /**
   * Get the preferred video input device
   */
  get preferredVideoDevice(): string | undefined {
    return this.get().preferredVideoDevice;
  }

  /**
   * Get echo cancellation
   */
  get echoCancellation(): boolean | undefined {
    return this.get().echoCancellation;
  }

  /**
   * Get noise supression
   */
  get noiseSupression(): NoiseSuppresionState | undefined {
    return this.get().noiseSupression;
  }

  /**
   * Get noise suppression strength (DeepFilterNet3 only)
   */
  get noiseSuppressionStrength(): number {
    return this.get().noiseSuppressionStrength;
  }

  /**
   * Get auto gain control
   */
  get autoGainControl(): boolean | undefined {
    return this.get().autoGainControl;
  }

  get noiseGate(): boolean {
    return this.get().noiseGate;
  }

  get pushToTalk(): boolean {
    return this.get().pushToTalk;
  }

  get pushToTalkKey(): string | undefined {
    return this.get().pushToTalkKey;
  }

  get pushToMute(): boolean {
    return this.get().pushToMute;
  }

  get pushToMuteKey(): string | undefined {
    return this.get().pushToMuteKey;
  }

  /**
   * Get screen share quality
   */
  get screenShareQuality(): ScreenShareQualityName | undefined {
    return this.get().screenShareQuality;
  }

  /**
   * Get screen share quality always ask
   */
  get screenShareQualityAsk(): boolean {
    return this.get().screenShareQualityAsk;
  }

  /**
   * Get screen share audio
   */
  get screenShareAudio(): boolean {
    return this.get().screenShareAudio;
  }

  get directStream(): boolean {
    return this.get().directStream ?? true;
  }

  get streamViewers(): number {
    return clampStreamViewers(this.get().streamViewers ?? 5);
  }

  /**
   * Get input volume
   */
  get inputVolume(): number {
    return this.get().inputVolume;
  }

  /**
   * Get output volume
   */
  get outputVolume(): number {
    return this.get().outputVolume;
  }

  /**
   * Get deafen status
   */
  get deafen(): boolean {
    return this.get().deafen;
  }

  /**
   * Get mic status
   */
  get micOn(): boolean {
    return this.get().micOn;
  }
}
